import type { ResidentDetailResponse } from '../../types/resident'
import { parseCalculationAuditNotes } from '../../utils/residentCalculationAuditNotes'
import type { CalculationAuditNotesMap } from '../../utils/residentCalculationAuditNotes.types'
import styles from './ResidentCalculationAuditBlocks.module.css'

/**
 * “Edited by …” line under calculation-tab dates (matches Angular audit notes).
 */
export function AuditBlock({
  field,
  residentDetail,
  publicNotesAndMessages,
  auditNotes,
}: {
  field:
    | 'rental_status_date'
    | 'movein_date'
    | 'new_resident_lease_start_date'
    | 'adjusted_notice'
    | 'adjust_max_movein_date'
    | 'first_month_not_paid'
  residentDefault?: Record<string, unknown> | null
  residentDetail?: ResidentDetailResponse | null
  publicNotesAndMessages?: Array<Record<string, unknown>> | null
  auditNotes?: CalculationAuditNotesMap
  unitKey?: Record<string, unknown>
}) {
  const notes = auditNotes || parseCalculationAuditNotes(publicNotesAndMessages)
  const rd = residentDetail?.resident_default
  const uk = (residentDetail?.unit_key || (residentDetail as unknown as Record<string, unknown>)?.unitKey) as
    | Record<string, unknown>
    | undefined

  const testId = `resident-calculation-audit-${field.replace(/_/g, '-')}`

  if (field === 'first_month_not_paid') {
    const hasFirstMonth =
      rd?.first_month_not_paid ||
      rd?.first_month_not_paid_month ||
      (residentDetail as unknown as Record<string, unknown>)?.first_month_not_paid
    if (!hasFirstMonth) return null
    const item = notes.firstmonthNotes
    const overrideItem = notes.firstmonthOverrideNotes
    if (!item?.name && !overrideItem?.name) return null

    return (
      <div data-testid={testId} className="mt-1">
        {item?.name ? (
          <p className={`${styles.auditNote} mb-1`}>
            {item.name} - {item.formattedDate}
          </p>
        ) : null}
        {overrideItem?.name ? (
          <p className={styles.auditNote}>
            <b>Liberty Override</b>
            <br />
            {overrideItem.name} - {overrideItem.formattedDate}
          </p>
        ) : null}
      </div>
    )
  }

  if (field === 'rental_status_date') {
    const item = notes.rentalStatusDateNotes
    if (!item?.name || !rd?.rental_status_date) return null
    return (
      <div data-testid={testId}>
        <p className={`${styles.auditNote} mb-1`}>
          Edited By {item.name} - {item.formattedDate}
        </p>
      </div>
    )
  }

  if (field === 'adjusted_notice') {
    const item = notes.adjustedNoticeNotes
    if (!item?.name || !rd?.adjusted_notice) return null
    return (
      <div data-testid={testId}>
        <p className={`${styles.auditNote} mb-1`}>
          Edited By {item.name} - {item.formattedDate}
        </p>
      </div>
    )
  }

  if (field === 'adjust_max_movein_date') {
    const item = notes.adjustMaxMovinNotes
    if (!item?.name || !rd?.adjust_max_movein_date) return null
    return (
      <div data-testid={testId}>
        <p className={`${styles.auditNote} mb-1`}>
          Edited By {item.name} - {item.formattedDate}
        </p>
      </div>
    )
  }

  if (field === 'new_resident_lease_start_date') {
    const item = notes.newResidentLeaseStartDateNotes
    const hasValue = uk?.new_resident_lease_start_date ?? rd?.new_resident_lease_start_date
    if (!item?.name || !hasValue) return null
    return (
      <div data-testid={testId}>
        <p className={styles.auditNote}>
          {item.name} - {item.formattedDate}
        </p>
      </div>
    )
  }

  if (field === 'movein_date') {
    const item = notes.moveindateNotes
    const hasValue = uk?.movein_date ?? rd?.movein_date
    if (!item?.name || !hasValue) return null
    return (
      <div data-testid={testId}>
        <p className={styles.auditNote}>
          {item.name} - {item.formattedDate}
        </p>
      </div>
    )
  }

  return null
}
